<template>
  <div class="custom-input">
    <label v-if="$validate.DataValid(label)" class="input-label">{{ $t(label) }}</label>
    <v-tooltip top :disabled="!showTooltip">
      <template v-slot:activator="{ on, attrs }">
        <v-select
          :value="fieldValue"
          outlined
          single-line
          :rules="rules"
          :readonly="isReadOnly"
          :placeholder="$t(placeholder)"
          :no-data-text="$t(noDataText)"
          :multiple="isMultiple"
          item-text="text"
          item-value="value"
          :items="options"
          @blur="updateValueOnBlur()"
          @input="updateValueOnChange($event)"
          :hide-details="hideDetails"
          :disabled="disabled"
          :dense="dense"
          :loading="isLoading"
          @click:clear="onClear()"
          :clearable="enableClear && !isReadOnly && !disabled"
          v-bind="attrs"
          v-on="on"
        >
          <template v-slot:append>
            <v-icon class="fi fi-rr-angle-small-down" size="18"></v-icon>
          </template>
          <template v-slot:selection="{ item, index }" v-if="isMultiple">
            <div v-if="showOthers || item.value === -1" class="v-select__selection v-select__selection--comma">
              {{ item.text }}{{ index !== fieldValue.length - 1 && item.value !== -1 ? ', ' : '' }}
            </div>
          </template>
        </v-select>
      </template>
      <span>{{ $t(placeholder) }}</span>
    </v-tooltip>
  </div>
</template>

<script>
export default {
  name: 'FormSelect',
  props: {
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    fieldValue: {
      type: String | Array,
      required: true,
      default: '',
    },
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    noDataText: {
      type: String,
      required: false,
      default: 'noOption',
    },
    isMultiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    dense: {
      type: Boolean,
      required: false,
      default: true,
    },
    dispatchUpdateOnChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    enableClear: {
      type: Boolean,
      required: false,
      default: false,
    },
    enableTooltip: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    isReadOnly() {
      return this.readonly || this.$store.getters.isLoading
    },
    rules() {
      return [
        v => {
          return this.required ? this.$validate.required(v) || this.$t('message.formRequired') : true
        },
      ]
    },
    showOthers() {
      if (this.isMultiple && this.fieldValue.includes(-1)) {
        return false
      }

      return true
    },
    showTooltip() {
      return this.enableTooltip && this.$validate.DataValid(this.fieldValue)
    },
  },
  data: () => ({
    isChange: false,
  }),
  methods: {
    updateValueOnBlur() {
      if (this.isMultiple && this.isChange) {
        this.$emit('changed', this.fieldValue)
        this.isChange = false
      }
    },
    updateValueOnChange(value) {
      let _value = value
      this.isChange = false
      if (this.isMultiple && this.options.find(el => el.value === -1)) {
        const allOptions = this.options.map(el => el.value)
        const validFieldValues = this.fieldValue ? this.fieldValue.filter(el => allOptions.includes(el)) : []

        _value = value.filter(el => allOptions.includes(el))

        const foundAll = _value.findIndex(el => el === -1)
        if (foundAll > -1) {
          if (_value.length !== this.options.length) {
            if (_value.length > validFieldValues.length && _value[_value.length - 1] === -1) {
              _value = this.options.map(el => el.value)
            } else {
              _value.splice(foundAll, 1)
            }
          }
        } else {
          if (this.fieldValue.includes(-1)) {
            _value = []
          } else if (_value.length === this.options.length - 1) {
            _value.push(-1)
          }
        }
      }

      if (this.isMultiple && Array.isArray(this.fieldValue)) {
        if (this.fieldValue.length !== _value.length) {
          this.isChange = true
        } else {
          for (let i = 0; i < this.fieldValue.length; i++) {
            const found = _value.find(value => value === this.fieldValue[i])
            if (!found) {
              this.isChange = true
              break
            }
          }
        }
      } else {
        this.isChange = true
      }

      this.$emit('update:fieldValue', _value)
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }

      if (!this.isMultiple) {
        this.$emit('changed', _value)
      }
    },
    onClear() {
      if (this.isMultiple) {
        this.$emit('update:fieldValue', [])
        this.$emit('changed', [])
      } else {
        this.$emit('update:fieldValue', null)
        // this.$emit('changed', null)
      }
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-text-field {
  & .v-input__append-inner > .v-input__icon--clear {
    margin-top: -4px !important;
  }
}
</style>
